'use client';

import Link from 'next/link';
import type { TradeInput } from '../../trade/schema';
import { documentLabels, type DocumentType } from '../../document/document-types';
import type { DocumentCompletion } from '../document-completion';
import { supportsDocumentField } from '../../document/field-policy';

export function FollowupPanel({ input, completion, documentType, documentId, tradeId, pending, questions, changes, onInsert }: Readonly<{
  input: TradeInput;
  completion: DocumentCompletion;
  documentType: DocumentType;
  documentId: string;
  tradeId: string;
  pending: boolean;
  questions: readonly string[];
  changes: readonly string[];
  onInsert(): void;
}>) {
  const missing = completion.required.length;
  const needsAttention = missing > 0 || questions.length > 0;
  const name = documentLabels[documentType];
  const hasField = (field: string) => supportsDocumentField(documentType, field);
  return <section className="followup-panel" aria-labelledby="followup-title" aria-busy={pending}>
    <div className="chat-section-heading"><h2 id="followup-title">{missing ? `还需补充 ${missing} 项信息` : questions.length ? '请确认补充内容' : '核心信息已补齐'}</h2><span className={`status-badge ${needsAttention ? 'status-draft' : 'status-success'}`}>{needsAttention ? '待补充' : '可生成'}</span></div>
    <p className="followup-help">已识别的信息已暂存到同一份草稿。在下方继续补充或修改即可，无需重复之前的内容。</p>
    <details className="followup-recognized" open><summary>已识别信息</summary>
      <dl>{hasField('buyer.name') && input.buyer.name ? <div><dt>客户名称</dt><dd>{input.buyer.name}</dd></div> : null}
        {hasField('supplier.name') && input.supplier.name ? <div><dt>供应商</dt><dd>{input.supplier.name}</dd></div> : null}
        {hasField('currency') && input.currency ? <div><dt>币种</dt><dd>{input.currency}</dd></div> : null}
        {input.consignee ? <div><dt>收货人</dt><dd>{input.consignee}</dd></div> : null}
        {input.assured ? <div><dt>被保险人</dt><dd>{input.assured}</dd></div> : null}
        {input.applicant ? <div><dt>申请人</dt><dd>{input.applicant}</dd></div> : null}
        {hasField('items') ? input.items.map((item, index) => {
          const amount = hasField('items.quantity') && item.quantity ? `${item.quantity} ${item.unit ?? ''}`
            : hasField('items.packages') && item.packages ? item.packages
              : hasField('items.grossWeight') ? item.grossWeight : null;
          return <div key={item.id}><dt>第 {index + 1} 项</dt><dd>{item.name ?? item.description ?? '商品名待补充'}{hasField('items.model') && item.model ? `（${item.model}）` : ''}{amount ? ` · ${amount}` : ''}{hasField('items.unitPrice') && item.unitPrice !== null ? ` · 单价 ${item.unitPrice}` : ''}</dd></div>;
        }) : null}
      </dl>
    </details>
    {changes.length ? <p className="followup-changes" role="status">本次更新：{changes.join('；')}</p> : null}
    {missing ? <ul className="followup-fields">{completion.required.map(field => <li key={field.key}><strong>{field.label}</strong><span>{field.question}</span>{field.manual ? <Link href={`/trades/${tradeId}?edit=1&document=${documentId}`}>前往编辑</Link> : null}</li>)}</ul> : null}
    {questions.length ? <div className="proposal-notice proposal-missing" role="status">{questions.map(question => <p key={question}>{question}</p>)}</div> : null}
    {needsAttention && completion.recommended.length ? <details className="followup-recommended"><summary>建议补充 {completion.recommended.length} 项（可稍后填写）</summary><ul>{completion.recommended.map(field => <li key={field.key}>{field.label}：{field.question}</li>)}</ul></details> : null}
    <div className="followup-actions">
      {completion.required.some(field => !field.manual) || needsAttention && !missing && completion.recommended.length ? <button className="secondary-button" type="button" disabled={pending} onClick={onInsert}>插入待补字段</button> : null}
      {pending ? <span role="status">正在更新草稿…</span> : <Link className={needsAttention ? 'secondary-button' : 'primary-button'} href={`/documents/${documentId}`}>{needsAttention ? '查看待补充预览' : `生成${name}`}</Link>}
    </div>
  </section>;
}
